import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link } from "react-router-dom";
import MainHeader from "../../components/MainHeader";
import { useAuth } from "../../hooks/auth/AuthContext";
import Rodape from "../../components/Rodape";
import HeaderSecti from "../../components/HeaderSecti";
import MfaStep from "./MfaStep";

// Tela de login redesenhada a partir do artboard "Login Oxetech" do Claude
// Design, opção 1b ("Cartão central"), escolhida pelo Marcelo em 03/09/2026.
//
// A opção 1b foi desenhada justamente para MANTER o topo institucional e a
// navegação que já existem — por isso HeaderSecti, MainHeader e Rodape seguem
// aqui e só o miolo foi refeito. Os valores abaixo (cores, alturas, raios,
// sombras) são os do arquivo do design, não aproximações.
const COR = {
  fundo: "#FFF9EF",
  circuloA: "#FCEFD6",
  circuloB: "#FBE7D3",
  cartaoBorda: "#F0E6D6",
  titulo: "#1F1E1C",
  textoSuave: "#6B655D",
  rotulo: "#3B3833",
  campoFundo: "#FDFBF7",
  campoBorda: "#E6DDCE",
  laranja: "#D2551D",
  laranjaHover: "#B8460F",
  azul: "#2C5FA8",
  iconeSenha: "#8C8478",
  iconeSenhaHover: "#F4EDE2",
};

const FONTE = "'Poppins', system-ui, sans-serif";

// Campo do design: 54px de altura, cantos 11px, fundo levemente creme, e no
// foco a borda vira azul com halo. Encapsulado aqui para os dois campos.
const CampoTexto = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    height: 54,
    borderRadius: 11,
    backgroundColor: COR.campoFundo,
    fontFamily: FONTE,
    fontSize: 16,
    color: COR.titulo,
    transition: "background-color .15s, box-shadow .15s",
    "& fieldset": {
      borderWidth: 1.5,
      borderColor: COR.campoBorda,
    },
    "&:hover fieldset": {
      borderColor: COR.campoBorda,
    },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: `0 0 0 4px rgba(44, 95, 168, .13)`,
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1.5,
      borderColor: COR.azul,
    },
    "&.Mui-error fieldset": {
      borderColor: COR.laranja,
    },
  },
  "& .MuiInputBase-input": {
    padding: "0 16px",
    fontFamily: FONTE,
    "&::placeholder": {
      color: "#A79E92",
      opacity: 1,
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: FONTE,
    fontSize: 12.5,
    marginLeft: 2,
    marginTop: 6,
    color: COR.laranja,
  },
});

const Rotulo = ({ children }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: FONTE,
      fontSize: 13,
      fontWeight: 600,
      color: COR.rotulo,
      mb: "7px",
      display: "block",
    }}
  >
    {children}
  </Typography>
);

const LinkTexto = styled(Link)({
  fontFamily: FONTE,
  color: COR.laranja,
  textDecoration: "none",
  "&:hover": { color: "#A83D0C", textDecoration: "underline" },
});

function SignInAluno2() {
  const [credential, setCredential] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [credentialError, setCredentialError] = useState("");

  // `mfaRequired` so existe no AuthContext de PRODUCAO (o de testes nao tem MFA).
  // Aqui ele vem `undefined`, o branch abaixo nunca dispara, e nada muda no fluxo
  // de testes. Em prod, se o backend pedir 2o fator, a tela passa a bola pro
  // MfaStep -- sem isso o usuario clicava em ENTRAR e NADA acontecia.
  const { login, mfaRequired } = useAuth();

  const handleCredentialChange = (e) => {
    const { value } = e.target;
    setCredential(value);
    if (/[^0-9./-]/.test(value)) {
      setCredentialError("Apenas números são permitidos.");
    } else {
      setCredentialError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!credential?.trim()) {
      newErrors.credential = "Campo obrigatório.";
    }
    if (!password?.trim()) {
      newErrors.password = "A Senha é obrigatória.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await login({ credential: credential.trim(), password });
    } catch (err) {
      // login handler trata o erro internamente
    } finally {
      setLoading(false);
    }
  };

  // 2o fator: o MfaStep e autocontido (pega verifyMfa/cancelMfa do proprio
  // useAuth e traz o proprio fundo e cartao), entao aqui so entra o cromo da
  // pagina, igual ao do fluxo normal.
  if (mfaRequired) {
    return (
      <>
        <HeaderSecti />
        <MainHeader page="login" />
        <MfaStep />
        <Rodape />
      </>
    );
  }

  return (
    <>
      <HeaderSecti />
      <MainHeader page="login" />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: COR.fundo,
          minHeight: { xs: "auto", md: "72vh" },
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 5, md: 8 },
          px: 2,
        }}
      >
        {/* Círculos decorativos do design. aria-hidden e pointerEvents none para
            não entrarem na navegação por leitor de tela nem capturarem clique. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: -120,
            top: 60,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: COR.circuloA,
            pointerEvents: "none",
            display: { xs: "none", md: "block" },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            right: -90,
            bottom: -90,
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: COR.circuloB,
            pointerEvents: "none",
            display: { xs: "none", md: "block" },
          }}
        />

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 520,
            background: "#fff",
            borderRadius: "20px",
            border: `1px solid ${COR.cartaoBorda}`,
            boxShadow: "0 22px 50px rgba(70, 40, 10, .13)",
            p: { xs: "32px 24px", sm: "44px 48px" },
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", textAlign: "center" }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: FONTE,
                fontSize: { xs: 26, sm: 30 },
                fontWeight: 700,
                letterSpacing: "-.01em",
                color: COR.titulo,
              }}
            >
              Acesso à plataforma
            </Typography>
            <Typography
              sx={{ fontFamily: FONTE, fontSize: 15, color: COR.textoSuave }}
            >
              Entre com seu CPF ou CNPJ para continuar
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box component="label">
              <Rotulo>CPF ou CNPJ</Rotulo>
              <CampoTexto
                fullWidth
                placeholder="000.000.000-00"
                value={credential}
                onChange={handleCredentialChange}
                error={!!errors.credential || !!credentialError}
                helperText={errors.credential || credentialError}
                inputProps={{ inputMode: "numeric" }}
              />
            </Box>

            <Box component="label">
              <Rotulo>Senha</Rotulo>
              <CampoTexto
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "9px",
                          color: COR.iconeSenha,
                          "&:hover": {
                            background: COR.iconeSenhaHover,
                            color: COR.azul,
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <LinkTexto to="/forgot" style={{ fontSize: 14, fontWeight: 500 }}>
                Esqueci minha senha
              </LinkTexto>
            </Box>

            <Button
              type="submit"
              disabled={loading}
              sx={{
                height: 56,
                borderRadius: "11px",
                background: COR.laranja,
                color: "#fff",
                fontFamily: FONTE,
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                boxShadow: "0 10px 22px rgba(210, 85, 29, .26)",
                "&:hover": { background: COR.laranjaHover },
                "&:active": { transform: "translateY(1px)" },
                "&.Mui-disabled": { background: "#E0A183", color: "#fff" },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Entrar"}
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography
              sx={{
                textAlign: "center",
                fontFamily: FONTE,
                fontSize: 15,
                color: COR.textoSuave,
              }}
            >
              Ainda não tem conta?{" "}
              <LinkTexto to="/select-cadastro" style={{ fontWeight: 600 }}>
                Cadastre-se
              </LinkTexto>
            </Typography>

            <Typography
              sx={{
                textAlign: "center",
                fontFamily: FONTE,
                fontSize: 14,
                color: COR.textoSuave,
              }}
            >
              <LinkTexto to="/ajuda">Precisa de ajuda?</LinkTexto>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Rodape />
    </>
  );
}

export default SignInAluno2;
